import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableLongStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.ImageLoader
import coil3.compose.AsyncImage
import coil3.compose.LocalPlatformContext
import coil3.svg.SvgDecoder
import com.zaurh.mapquizwebsite.domain.model.DailyRewardDO
import com.zaurh.mapquizwebsite.domain.model.DailyRewardStatus
import com.zaurh.mapquizwebsite.presentation.screen.daily_reward.DailyRewardsAction
import com.zaurh.mapquizwebsite.presentation.screen.daily_reward.DailyRewardsAction.OnClaimReward
import com.zaurh.mapquizwebsite.presentation.screen.daily_reward.DailyRewardsViewModel
import com.zaurh.mapquizwebsite.utils.getOutfitFont
import kotlinx.coroutines.delay
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun DailyRewardsScreen(
    viewModel: DailyRewardsViewModel = koinViewModel(),
) {
    val uiState by viewModel.uiState.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.onAction(DailyRewardsAction.OnInit)
    }

    val nextReward =
        uiState.dailyRewards.firstOrNull { it.status != DailyRewardStatus.OBTAINED.name }

    BoxWithConstraints(
        modifier = Modifier
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.background)
    ) {
        val maxWidth = maxWidth

        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .padding(32.dp)
        ) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(10.dp))
                    .background(MaterialTheme.colorScheme.surface)
                    .padding(12.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = "Daily rewards",
                    fontFamily = getOutfitFont(),
                    fontSize = 20.sp
                )

                if (uiState.nextClaimAt != null) {
                    Column {
                        Text(
                            text = "Next reward in",
                            fontFamily = getOutfitFont()
                        )

                        DailyRewardTimer(
                            nextClaimAt = uiState.nextClaimAt!!
                        )
                    }
                }
            }

            Spacer(modifier = Modifier.height(24.dp))

            val maxItems = when (maxWidth) {
                in 0.dp..640.dp -> 3
                in 640.dp..800.dp -> 4
                else -> 5
            }

            FlowRow(
                modifier = Modifier.fillMaxWidth(),
                maxItemsInEachRow = maxItems,
                horizontalArrangement = Arrangement.SpaceAround,
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                uiState.dailyRewards.forEach { reward ->
                    DailyRewardItem(
                        dailyRewardDO = reward,
                        isNextReward = reward == nextReward,
                        nextClaimAt = uiState.nextClaimAt,
                        onClaim = { viewModel.onAction(OnClaimReward) })
                }
            }
        }
    }
}

@Composable
fun DailyRewardItem(
    dailyRewardDO: DailyRewardDO,
    isNextReward: Boolean,
    nextClaimAt: Long?,
    onClaim: () -> Unit
) {
    val context = LocalPlatformContext.current
    val imageLoader =
        remember { ImageLoader.Builder(context).components { add(SvgDecoder.Factory()) }.build() }
    val isClaimable = dailyRewardDO.status == DailyRewardStatus.CLAIM.name
    val showTimer =
        isNextReward && !isClaimable && nextClaimAt != null
    Column(
        horizontalAlignment =
            Alignment.CenterHorizontally
    ) {
        Text(
            text = "Day ${dailyRewardDO.day}",
            fontFamily = getOutfitFont()
        )
        Box {
            AsyncImage(
                model = dailyRewardDO.image,
                contentDescription = null,
                modifier = Modifier.size(120.dp),
                imageLoader = imageLoader
            )
            Text(
                modifier =
                    Modifier.align(Alignment.BottomEnd).clip(RoundedCornerShape(10.dp))
                        .background(Color.LightGray)
                        .padding(4.dp),
                text = "x${dailyRewardDO.amount}",
                fontFamily = getOutfitFont()
            )
        }
        Text(
            text =
                dailyRewardDO.name, fontFamily = getOutfitFont()
        )
        Button(
            modifier = Modifier.pointerHoverIcon(
                if (isClaimable) {
                    PointerIcon.Hand
                } else {
                    PointerIcon.Default
                }
            ), enabled = isClaimable, onClick = onClaim
        ) {
            when {
                isClaimable -> {
                    Text("CLAIM")
                }

                showTimer -> {
                    DailyRewardTimer(nextClaimAt = nextClaimAt)
                }

                else -> {
                    Text(dailyRewardDO.status.toString())
                }
            }
        }
    }
}

@Composable
fun DailyRewardTimer(nextClaimAt: Long) {
    var remainingMillis by remember(nextClaimAt) {
        mutableLongStateOf(
            (nextClaimAt - currentTimeMillis()).coerceAtLeast(
                0
            )
        )
    }

    LaunchedEffect(nextClaimAt) {
        while (remainingMillis > 0) {
            delay(1000)
            remainingMillis = (nextClaimAt - currentTimeMillis()).coerceAtLeast(0)
        }
    }
    val remainingSeconds = remainingMillis / 1000
    val hours = remainingSeconds / 3600
    val minutes = (remainingSeconds % 3600) / 60
    val seconds = remainingSeconds % 60

    Text(
        text = "${hours.toString().padStart(2, '0')}:${
            minutes.toString().padStart(2, '0')
        }:${seconds.toString().padStart(2, '0')}",
        fontFamily = getOutfitFont(),
        fontSize = 14.sp
    )
}

private fun currentTimeMillis(): Long {
    return kotlin.time.Clock.System.now().toEpochMilliseconds()
}